import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from 'auth/AuthProvider';
import { PERMISSIONS } from 'auth/permissions';
import { queryKeys } from 'core/api/queryKeys';
import { useI18n } from 'core/i18n/I18nProvider';
import { paths } from 'core/routing/paths';
import { assessmentApi } from 'features/assessment-dashboard/assessment.api';
import { ApiError } from 'core/errors/ApiError';
import { humanizeApiError, mapIndexedWeightErrors } from 'core/errors/mapFieldErrors';
import { setExpectedRevision } from 'core/api/revisionStore';
import { Button } from 'shared/components/Button';
import { Badge, ConflictAlert, ErrorState, PageHeader, Skeleton } from 'shared/components/Feedback';
import { FilterBar } from 'shared/components/FilterBar';
import { useToast } from 'shared/components/Toast';
import { WeightInput } from 'shared/forms/Fields';
import { formatSignedWeight, isValidWeight, localizedName, parseWeight } from 'shared/utilities/format';
import { useUnsavedChanges } from 'shared/utilities/useUnsavedChanges';
import { useIsDesktop } from 'layout/useMediaQuery';

const WINDOW_THRESHOLD = 400;
const WINDOW_SIZE = 80;

function statusTone(status, weight) {
  if (!status || status === 'pending') {
    return 'warning';
  }
  if (status === 'zero_initialized') {
    return 'warning';
  }
  if (status === 'configured' && Number(weight) === 0) {
    return 'neutral';
  }
  return 'success';
}

function statusLabel(t, status, weight) {
  if (!status) {
    return t('weights.missing');
  }
  if (status === 'pending') {
    return t('weights.pending');
  }
  if (status === 'zero_initialized') {
    return t('weights.zeroInitialized');
  }
  if (status === 'configured' && Number(weight) === 0) {
    return t('weights.explicitZero');
  }
  if (status === 'configured') {
    return t('weights.configuredNonzero');
  }
  return t('weights.unconfigured');
}

function isPendingWeightStatus(status) {
  return !status || status === 'pending';
}

function buildWeightMatrixPayload(drafts, rows) {
  const payload = Object.entries(drafts).map(([assessment_answer_id, weight]) => ({
    assessment_answer_id: Number(assessment_answer_id),
    weight: parseWeight(weight),
  }));
  const draftedIds = new Set(payload.map((row) => row.assessment_answer_id));
  rows.forEach(({ answer }) => {
    if (!draftedIds.has(answer.id) && isPendingWeightStatus(answer.configuration_status)) {
      payload.push({
        assessment_answer_id: answer.id,
        weight: 0,
      });
    }
  });
  return payload;
}

export function WeightsLandingPage() {
  const { t, locale } = useI18n();
  const query = useQuery({ queryKey: queryKeys.patterns, queryFn: ({ signal }) => assessmentApi.listPatterns({ signal }) });
  if (query.isLoading) return <Skeleton rows={5} />;
  if (query.isError) return <ErrorState title={humanizeApiError(query.error, t)} onRetry={query.refetch} retryLabel={t('actions.retry')} />;
  const items = query.data?.items || [];
  return (
    <div>
      <PageHeader title={t('weights.title')} />
      {items.length === 0 ? <p>{t('empty.patterns')}</p> : (
        <ul className="pattern-pick-list">
          {items.map((pattern) => (
            <li key={pattern.id}>
              <Link to={paths.weightMatrix(pattern.id)}>
                <span className="color-dot" style={{ background: pattern.color }} />
                {localizedName(pattern, locale)}
                {pattern.has_pending_zero_warning ? <em>{t('patterns.zeroWarning')}</em> : null}
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function WeightMatrixPage() {
  const { id } = useParams();
  const [params, setParams] = useSearchParams();
  const { t, locale } = useI18n();
  const { hasPermission } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const canEdit = hasPermission(PERMISSIONS.weightsManage);
  const desktop = useIsDesktop();
  const sectionId = params.get('section_id') || '';
  const [search, setSearch] = useState('');
  const [configFilter, setConfigFilter] = useState('all');
  const [drafts, setDrafts] = useState({});
  const [errors, setErrors] = useState({});
  const [conflict, setConflict] = useState(null);
  const [windowStart, setWindowStart] = useState(0);
  const submittedRef = useRef([]);
  const dirty = Object.keys(drafts).length > 0;
  useUnsavedChanges(dirty);

  const query = useQuery({
    queryKey: queryKeys.weightMatrix(id, sectionId || null),
    queryFn: ({ signal }) => assessmentApi.weightMatrix(id, sectionId || null, { signal }),
  });

  useEffect(() => {
    setErrors({});
    setWindowStart(0);
  }, [id, sectionId, query.dataUpdatedAt]);

  const save = useMutation({
    mutationFn: (weights) => assessmentApi.saveWeightMatrix(id, weights),
    onSuccess: async (data) => {
      setDrafts({});
      setConflict(null);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['assessment', 'weight-matrix'] }),
        queryClient.invalidateQueries({ queryKey: queryKeys.patterns }),
        queryClient.invalidateQueries({ queryKey: queryKeys.overview }),
      ]);
      toast.success(t('status.saved'));
      if ((data?.progress?.pending_answers ?? 0) > 0) {
        toast.info(t('weights.pendingRemaining'));
      }
    },
    onError: async (error) => {
      if (error instanceof ApiError && error.isStaleRevision()) {
        setConflict(error.errors);
        if (error.errors?.current_revision) {
          setExpectedRevision(error.errors.current_revision);
        }
        await query.refetch();
        toast.error(t('weights.staleConflict'));
        return;
      }
      if (error instanceof ApiError && error.isValidation()) {
        setErrors(mapIndexedWeightErrors(error.errors, submittedRef.current));
      }
      toast.error(humanizeApiError(error, t));
    },
  });

  const sections = useMemo(() => query.data?.sections || [], [query.data]);
  const progress = query.data?.progress || {};
  const pattern = query.data?.pattern || {};

  const rows = useMemo(() => {
    const collected = [];
    sections.forEach((section) => {
      (section.questions || []).forEach((question) => {
        (question.answers || []).forEach((answer) => {
          collected.push({ section, question, answer });
        });
      });
    });
    return collected.filter(({ section, question, answer }) => {
      const haystack = `${section.title_ar} ${section.title_en} ${question.text_ar} ${question.text_en} ${answer.text_ar} ${answer.text_en}`.toLowerCase();
      const matchesSearch = haystack.includes(search.trim().toLowerCase());
      const status = answer.configuration_status;
      const configured = status === 'configured';
      const matchesConfig = configFilter === 'all' || (configFilter === 'configured' ? configured : !configured);
      return matchesSearch && matchesConfig;
    });
  }, [configFilter, search, sections]);

  if (query.isLoading) return <Skeleton rows={8} />;
  if (query.isError) return <ErrorState title={humanizeApiError(query.error, t)} onRetry={query.refetch} retryLabel={t('actions.retry')} />;

  const shouldWindow = rows.length > WINDOW_THRESHOLD;
  const visibleRows = shouldWindow ? rows.slice(windowStart, windowStart + WINDOW_SIZE) : rows;
  const hasPendingToCommit = rows.some(({ answer }) => (
    isPendingWeightStatus(answer.configuration_status)
    && !Object.prototype.hasOwnProperty.call(drafts, answer.id)
  ));
  const canSave = dirty || hasPendingToCommit;

  const grouped = visibleRows.reduce((acc, row) => {
    const key = row.section.id;
    acc[key] = acc[key] || { section: row.section, items: [] };
    acc[key].items.push(row);
    return acc;
  }, {});

  return (
    <div>
      <PageHeader
        title={`${t('weights.title')} · ${localizedName(pattern, locale) || pattern.slug || id}`}
        description={t('weights.signedHint')}
        actions={canEdit ? (
          <Button
            disabled={save.isPending || !canSave}
            loading={save.isPending}
            onClick={() => {
              const weights = buildWeightMatrixPayload(drafts, rows);
              if (weights.length === 0) {
                return;
              }
              if (weights.some((row) => !isValidWeight(row.weight))) {
                toast.error(t('errors.validation'));
                return;
              }
              submittedRef.current = weights;
              save.mutate(weights);
            }}
          >
            {t('actions.save')}
          </Button>
        ) : null}
      />
      {pattern.has_pending_zero_warning ? <p className="warning-banner">{t('patterns.zeroWarning')}</p> : null}
      {(progress.pending_answers ?? 0) > 0 ? <p className="warning-banner" role="status">{t('weights.pendingRemaining')}</p> : null}
      <ConflictAlert message={conflict ? t('weights.staleConflict') : null} />
      <div className="progress-bar" aria-label={t('patterns.progress')}>
        <span>{t('patterns.totalAnswers')}: {progress.total_answers ?? 0}</span>
        <span>{t('patterns.configured')}: {progress.configured_answers ?? 0}</span>
        <span>{t('patterns.remaining')}: {progress.pending_answers ?? 0}</span>
        {progress.zero_initialized_answers != null ? (
          <span>{t('weights.zeroInitialized')}: {progress.zero_initialized_answers}</span>
        ) : null}
      </div>
      <FilterBar>
        <label className="field" htmlFor="weight-section">
          <span className="field-label">{t('weights.filterSection')}</span>
          <select id="weight-section" className="input" value={sectionId} onChange={(event) => setParams(event.target.value ? { section_id: event.target.value } : {})}>
            <option value="">{t('weights.filterSection')}</option>
            {sections.map((section) => (
              <option key={section.id} value={section.id}>{localizedName(section, locale)}</option>
            ))}
          </select>
        </label>
        <label className="field filter-search" htmlFor="weight-search">
          <span className="field-label">{t('actions.search')}</span>
          <input id="weight-search" className="input" value={search} onChange={(event) => setSearch(event.target.value)} placeholder={t('weights.search')} />
        </label>
        <label className="field" htmlFor="weight-config">
          <span className="field-label">{t('actions.filter')}</span>
          <select id="weight-config" className="input" value={configFilter} onChange={(event) => setConfigFilter(event.target.value)}>
            <option value="all">{t('weights.all')}</option>
            <option value="configured">{t('weights.configured')}</option>
            <option value="unconfigured">{t('weights.unconfigured')}</option>
          </select>
        </label>
      </FilterBar>
      {shouldWindow ? (
        <div className="toolbar">
          <span>{t('weights.pageSize')}: {windowStart + 1}–{Math.min(windowStart + WINDOW_SIZE, rows.length)} / {rows.length}</span>
          <Button tone="ghost" disabled={windowStart === 0} onClick={() => setWindowStart((value) => Math.max(0, value - WINDOW_SIZE))}>{t('pagination.previous')}</Button>
          <Button tone="ghost" disabled={windowStart + WINDOW_SIZE >= rows.length} onClick={() => setWindowStart((value) => value + WINDOW_SIZE)}>{t('pagination.next')}</Button>
        </div>
      ) : null}
      {Object.values(grouped).map((group) => (
        <section key={group.section.id} className="matrix-group">
          <h2>{localizedName(group.section, locale)}</h2>
          {desktop ? (
            <div className="table-wrap">
              <table className="data-table weight-grid">
                <caption className="sr-only">{localizedName(pattern, locale)} · {localizedName(group.section, locale)}</caption>
                <thead>
                  <tr>
                    <th className="sticky-col">{t('questions.answers')}</th>
                    <th>{t('consultations.status')}</th>
                    <th>{t('questions.weights')}</th>
                    <th>{t('questions.weights')}</th>
                  </tr>
                </thead>
                <tbody>
                  {group.items.map(({ question, answer }) => {
                    const draft = Object.prototype.hasOwnProperty.call(drafts, answer.id) ? drafts[answer.id] : null;
                    const display = draft == null ? (answer.weight == null ? '' : answer.weight) : draft;
                    return (
                      <tr key={answer.id} className={errors[`weights.${answer.id}.weight`] ? 'input-error' : undefined}>
                        <th className="sticky-col" scope="row">
                          <p className="muted">{localizedName(question, locale)}</p>
                          <Link to={paths.answerDetail(answer.id)}>{localizedName(answer, locale)}</Link>
                        </th>
                        <td>
                          <Badge tone={statusTone(answer.configuration_status, answer.weight)}>
                            {statusLabel(t, answer.configuration_status, answer.weight)}
                          </Badge>
                        </td>
                        <td>
                          <WeightInput
                            id={`weight-${answer.id}`}
                            value={display}
                            onChange={(value) => setDrafts((current) => ({ ...current, [answer.id]: value }))}
                            error={errors[`weights.${answer.id}.weight`]?.[0]}
                            disabled={!canEdit || save.isPending}
                          />
                        </td>
                        <td className="ltr-id">{formatSignedWeight(display)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="weight-card-list">
              {group.items.map(({ question, answer }) => {
                const draft = Object.prototype.hasOwnProperty.call(drafts, answer.id) ? drafts[answer.id] : null;
                const display = draft == null ? (answer.weight == null ? '' : answer.weight) : draft;
                return (
                  <article key={answer.id} className="card">
                    <p className="muted">{localizedName(question, locale)}</p>
                    <Link to={paths.answerDetail(answer.id)}>{localizedName(answer, locale)}</Link>
                    <Badge tone={statusTone(answer.configuration_status, answer.weight)}>
                      {statusLabel(t, answer.configuration_status, answer.weight)}
                    </Badge>
                    <WeightInput
                      id={`weight-${answer.id}`}
                      label={localizedName(pattern, locale)}
                      value={display}
                      onChange={(value) => setDrafts((current) => ({ ...current, [answer.id]: value }))}
                      error={errors[`weights.${answer.id}.weight`]?.[0]}
                      disabled={!canEdit || save.isPending}
                    />
                    <span className="ltr-id">{formatSignedWeight(display)}</span>
                  </article>
                );
              })}
            </div>
          )}
        </section>
      ))}
    </div>
  );
}
